import { FoodsIcon, NavbarButtonType, NavbarLinkType, NavbarDrawerType } from "@foodpilot/foods";
import { useNavigate } from "react-router-dom";
import { useClientSwitch } from "src/components/ClientSwitch/useClientSwitch";
import { CompaniesService } from "src/services/Companies/CompaniesService";
import { useTranslation } from "react-i18next";
import {
    BRAND,
    CAMPAIGN,
    COMPANY,
    COMPANY_SECTOR,
    CREATE,
    DELETE,
    INGREDIENT,
    INGREDIENT_LINE,
    MODIFY,
    PRODUCT,
    PRODUCT_LINE,
    SCORE_TYPE,
    SETTINGS,
    STRATEGY,
    useFoodpilotContext,
    USER,
    VIEW,
} from "src/context/FoodpilotContext.tsx";
import "./evea-askor.css";

// Icon missing in the Inexweb
import OpenBookIcon from "../../assets/icons/open-book.svg?react";
import { companySlug, SettingsSlug, URLHandler } from "src/services/URLHandler";
import { useApplicationParameters } from "src/api/client-api/appParams";

export const useClientButton = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const clientSwitch = useClientSwitch();
    const { isFeatureActivated, applicationParameters } = useApplicationParameters();

    const { canUser, user, selectedCompany } = useFoodpilotContext();

    const companyIdToRedirect = selectedCompany !== null ? selectedCompany.id : CompaniesService.getCurrentCompanyId();
    if (companyIdToRedirect === null) return [];

    const buttons: NavbarButtonType[] = [];

    // For now, the architecture of this file is mostly about client blocks.
    // Waiting for a real proper feature switching.
    // At least, we're now able to see the common usages.
    const homeButton: NavbarButtonType = {
        type: "topLink",
        label: t("Home"),
        icon: <FoodsIcon icon="home" size={3} />,
        isSelected: location.pathname === URLHandler.home(),
        action: () => {
            const url = URLHandler.home();
            navigate(url);
        },
    };
    if (isFeatureActivated("FS-homepage") || isFeatureActivated("FS-puck-homepage")) {
        buttons.push(homeButton);
    }
    // Foodpilot buttons
    const actionsUrl = URLHandler.company.actions.list(companyIdToRedirect);
    const isActionPlanSelected = location.pathname.includes(actionsUrl);

    const productListUrl = URLHandler.product.list(companyIdToRedirect);
    const isProductSelected = location.pathname.includes(productListUrl);

    const ingredientListUrl = URLHandler.ingredient.list(companyIdToRedirect);
    const isIngredientSelected = location.pathname.includes(ingredientListUrl);

    const supplierListUrl = URLHandler.supplier.list(companyIdToRedirect);
    const isSupplierSelected = location.pathname.includes(supplierListUrl);

    const isProductIngSuppSelect =
        isProductSelected || isIngredientSelected || isSupplierSelected || isActionPlanSelected;
    const isCompanySelected = location.pathname.includes(companySlug) && isProductIngSuppSelect === false;

    const innerDrawer: NavbarLinkType[] = [];
    const diagnosticButton = {
        label: t("Diagnostic"),
        icon: <FoodsIcon icon="diagnostic" size={3} />,
        isSelected: isCompanySelected,
        action: () => navigate(diagnosticUrl),
    };
    innerDrawer.push(diagnosticButton);

    if (isFeatureActivated("FS-actionPlan")) {
        const actionPlanButton = {
            label: t("sidebar.actionPlan"),
            icon: <FoodsIcon icon="trajectory" size={3} />,
            isSelected: isActionPlanSelected,

            action: () => navigate(actionsUrl),
        };
        innerDrawer.push(actionPlanButton);
    }

    const lustucru_Drawer: NavbarButtonType = {
        type: "drawer",
        label: t("sidebar.lustucru.company.drawer"),
        icon: <FoodsIcon icon="corporate" size={3} />,
        items: innerDrawer,
    };
    const lustucru__csCollective = clientSwitch({
        defaultOption: [],
        foodpilotLustucru: [lustucru_Drawer],
    });
    if (innerDrawer.length > 0) {
        buttons.push(...lustucru__csCollective);
    }

    const productListButton: NavbarButtonType = {
        type: "topLink",
        label: t("Produits"),
        icon: <FoodsIcon size={2} icon="products" />,
        isSelected: isProductSelected,
        action: () => navigate(productListUrl),
    };
    if (canUser(VIEW, PRODUCT)) {
        const _cs1 = clientSwitch({
            defaultOption: [],
            foodpilot: [productListButton],
            foodpilotLustucru: [productListButton],
        });
        buttons.push(..._cs1);
    }

    const ingredientListButton: NavbarButtonType = {
        type: "topLink",
        label: t("Ingrédients"),
        icon: <FoodsIcon size={2} icon="ingredients" />,
        isSelected: isIngredientSelected,
        action: () => navigate(ingredientListUrl),
    };
    if (canUser(VIEW, INGREDIENT)) {
        const _cs2 = clientSwitch({
            defaultOption: [],
            foodpilot: [ingredientListButton],
            foodpilotLustucru: [ingredientListButton],
        });
        buttons.push(..._cs2);
    }

    const supplierListButton: NavbarButtonType = {
        type: "topLink",
        label: t("Fournisseurs"),
        icon: <FoodsIcon size={2} icon="supplier" />,
        isSelected: isSupplierSelected,
        action: () => navigate(supplierListUrl),
    };
    if (canUser(VIEW, COMPANY)) {
        const _cs3 = clientSwitch({
            defaultOption: [],
            foodpilot: [supplierListButton],
            foodpilotLustucru: [supplierListButton],
        });
        buttons.push(..._cs3);
    }

    // Inexweb buttons

    if (user.trustedUser.roles.includes("ROLE_ADMIN")) {
        const bilanRSEButton: NavbarButtonType = {
            type: "topLink",
            label: "Bilan RSE",
            icon: <OpenBookIcon />,
            isSelected: location.pathname.includes("inexweb"),
            action: () => navigate("inexweb"),
        };
        const _csInexweb = clientSwitch({
            defaultOption: [],
            inexweb: [bilanRSEButton],
        });
        buttons.push(..._csInexweb);
    }

    // Collective buttons
    const diagnosticUrl = URLHandler.company.scores(companyIdToRedirect);
    const trajectoryUrl = URLHandler.company.actions.list(companyIdToRedirect);
    const isTrajectoryPath = location.pathname.includes(trajectoryUrl);
    const isDiagnosticPath = location.pathname.includes(companySlug) && isTrajectoryPath === false;

    const collectiveButtons: NavbarButtonType[] = [];
    const diagnosticCollectiveButton: NavbarButtonType = {
        type: "topLink",
        label: t("Diagnostic"),
        icon: <FoodsIcon icon="diagnostic" size={3} />,
        isSelected: isDiagnosticPath,
        action: () => navigate(diagnosticUrl),
    };
    collectiveButtons.push(diagnosticCollectiveButton);

    const trajectoryCollectiveButton: NavbarButtonType = {
        type: "topLink",
        label: t("sidebar.actionPlan"),
        icon: <FoodsIcon icon="trajectory" size={3} />,
        isSelected: isTrajectoryPath,
        action: () => navigate(trajectoryUrl),
    };
    if (isFeatureActivated("FS-actionPlan")) {
        collectiveButtons.push(trajectoryCollectiveButton);
    }
    const _csCollective = clientSwitch({
        defaultOption: [],
        collective: collectiveButtons,
    });
    buttons.push(..._csCollective);

    // BRED buttons
    // const isDiagnosticMatching = location.pathname.includes(companySlug);
    // const isTrajectoryMatching = location.pathname.includes(actionsUrl);

    // const bredButtons: NavbarButtonType[] = [];
    // const diagnosticBREDButton: NavbarButtonType = {
    //     type: "topLink",
    //     label: t("Diagnostic"),
    //     icon: <FoodsIcon icon="diagnostic" size={3} />,
    //     isSelected: isDiagnosticMatching && isTrajectoryMatching === false,
    //     action: () => {
    //         const url = URLHandler.company.synthesis(companyIdToRedirect);
    //         navigate(url);
    //     },
    // };
    // bredButtons.push(diagnosticBREDButton);

    // const trajectoryButton: NavbarButtonType = {
    //     type: "topLink",
    //     label: t("bred.path"),
    //     icon: <FoodsIcon icon="trajectory" size={3} />,
    //     isSelected: isTrajectoryMatching,
    //     action: () => {
    //         navigate(actionsUrl);
    //     },
    // };
    // if (isFeatureActivated("FS-actionPlan")) {
    //     bredButtons.push(trajectoryButton);
    // }

    // This button is only HIDDEN
    // Requested by Simon, to wait if "they ever need it"
    // Because they change their mind a lot. (no sarcasm)

    // const supportButton: NavbarButtonType = {
    //     type: "topLink",
    //     label: t("bred.support"),
    //     icon: <FoodsIcon icon="support" size={3} />,
    //     isSelected: location.pathname.includes(URLHandler.cms.list()),
    //     action: () => {
    //         const url = URLHandler.cms.list();
    //         navigate(url);
    //     },
    // };
    // bredButtons.push(supportButton);

    // const _csBRED = clientSwitch({
    //     defaultOption: [],
    //     bred: bredButtons,
    // });
    // buttons.push(..._csBRED);

    // Common buttons

    const csrStrategyButton: NavbarButtonType = {
        type: "topLink",
        label: "Strategy",
        icon: <FoodsIcon size={2} icon={"scoresIndicators"} />,
        isSelected: location.pathname.includes(URLHandler.strategy.list()),
        action: () => navigate(URLHandler.strategy.list()),
    };
    const documentationButton: NavbarButtonType = {
        type: "topLink",
        label: t("Documentation"),
        icon: <FoodsIcon size={2} icon="documentation" />,
        isSelected: location.pathname.includes(URLHandler.documentation()),
        action: () => navigate(URLHandler.documentation()),
    };
    const defaultButtons = [];

    if (isFeatureActivated("FS-strategy")) {
        defaultButtons.push(csrStrategyButton);
    }

    if (isFeatureActivated("FS-evea-askor")) {
        const eveaAskorButton: NavbarButtonType = {
            type: "topLink",
            label: t("evea.askor.menu.button"),
            icon: (
                <div>
                    <FoodsIcon size={2} icon="askor" />
                </div>
            ),
            isSelected: false,
            action: () => window.open(`//${applicationParameters?.eveaAskorUrl}`, "_blank")?.focus(),
        };
        defaultButtons.push(eveaAskorButton);
    }

    defaultButtons.push(documentationButton);

    const _cs5 = clientSwitch({
        defaultOption: defaultButtons,
        // bred: [],
        inexweb: [],
    });
    buttons.push(..._cs5);

    if (canUser(VIEW, SETTINGS)) {
        buttons.push({
            type: "topLink",
            label: t("Paramètres globaux"),
            icon: <FoodsIcon size={2} icon="settings" />,
            isSelected: location.pathname.includes(SettingsSlug),
            action: () => navigate(URLHandler.settings.list()),
        });
    }

    return buttons;
};
